/**
 * Level14Scene - 关卡14：红移蓝移
 * @description 教学目标：理解相对论多普勒效应
 * 核心公式：多普勒因子 D = √((1+β)/(1-β)) 或 √((1-β)/(1+β))
 * 其中 β = v/c，接近时蓝移，远离时红移
 */

import Phaser from 'phaser';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { Relativity, type DopplerResult } from '@/physics/Relativity.ts';

// 关卡14特定配置
interface Level14SpecificConfig {
  /** 可选速度列表（以光速为单位） */
  velocityOptions: number[];
  /** 公式 */
  formula: string;
  /** 提示 */
  hint: string;
  /** 目标多普勒因子范围 */
  targetDopplerMin: number;
  /** 目标多普勒因子最大 */
  targetDopplerMax: number;
  /** 成功要求：达到指定的多普勒效应 */
  successRequirement: string;
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

// 颜色常量（用于可视化）
const COLORS = {
  // 原始白光颜色
  white: { r: 255, g: 255, b: 255 },
  // 蓝移颜色范围
  blueShift: { r: 100, g: 150, b: 255 },
  // 红移颜色范围
  redShift: { r: 255, g: 100, b: 80 },
};

/**
 * Level14Scene 类
 * 关卡14场景，实现红移蓝移教学
 */
export class Level14Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level14SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  // 相对论物理模块
  private relativity: Relativity;

  // 当前选择
  private selectedVelocity: number = 0.3;
  private isApproaching: boolean = true;

  // 多普勒效应结果
  private currentDopplerResult: DopplerResult | null = null;

  // 游戏对象
  private starObject: Phaser.GameObjects.Graphics | null = null;
  private observerObject: Phaser.GameObjects.Graphics | null = null;
  private lightBeamGraphics: Phaser.GameObjects.Graphics | null = null;
  private colorPreviewGraphics: Phaser.GameObjects.Graphics | null = null;
  private spectrumBar: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private velocityButtons: Phaser.GameObjects.Graphics[] = [];
  private directionButtons: Phaser.GameObjects.Graphics[] = [];
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;

  // 数据显示
  private dopplerFactorText: Phaser.GameObjects.Text | null = null;
  private frequencyRatioText: Phaser.GameObjects.Text | null = null;
  private wavelengthRatioText: Phaser.GameObjects.Text | null = null;
  private colorDescriptionText: Phaser.GameObjects.Text | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  constructor() {
    super({ key: 'Level14Scene' });
    // 初始化相对论计算模块（使用游戏简化单位，光速 = 1）
    this.relativity = new Relativity(1.0);
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-14', 'src/levels/levels/level-14.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-14') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level14SpecificConfig;
    }

    // 创建背景
    this.createBackground();

    // 创建游戏场景
    this.createGameScene();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();

    // 初始化计算
    this.updateDopplerCalculation();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState === 'running') {
      // 更新时间
      this.elapsedTime += delta / 1000;

      // 动画效果：光源移动
      this.animateStarMovement();

      // 更新光束效果
      this.updateLightBeamEffect();
    }
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 宇宙深空背景
    this.cameras.main.setBackgroundColor('#0a0a1a');

    // 添加星星点缀（远处星空）
    const starField = this.add.graphics();
    for (let i = 0; i < 50; i++) {
      const x = Math.random() * GAME_WIDTH;
      const y = Math.random() * GAME_HEIGHT;
      const size = Math.random() * 2 + 0.5;
      const brightness = Math.random() * 0.5 + 0.3;

      starField.fillStyle(0xffffff, brightness);
      starField.fillCircle(x, y, size);
    }

    // 添加网格线作为参考（淡化）
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x1a1a3a, 0.2);

    // 垂直网格线
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    // 水平网格线
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }
  }

  /**
   * 创建游戏场景
   */
  private createGameScene(): void {
    // 创建光源（星星）
    this.createStar();

    // 创建观察者
    this.createObserver();

    // 创建光束可视化
    this.createLightBeam();

    // 创建颜色预览
    this.createColorPreview();

    // 创建光谱条
    this.createSpectrumBar();
  }

  /**
   * 创建光源（星星）
   */
  private createStar(): void {
    this.starObject = this.add.graphics();
    this.drawStar(150, GAME_HEIGHT / 2, 30, 0xffffff);

    // 标签
    this.add.text(150, GAME_HEIGHT / 2 + 60, '光源（星体）', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 绘制星星
   */
  private drawStar(x: number, y: number, size: number, color: number): void {
    if (!this.starObject) return;

    this.starObject.clear();

    // 光晕效果
    this.starObject.fillStyle(color, 0.1);
    this.starObject.fillCircle(x, y, size * 2);

    this.starObject.fillStyle(color, 0.3);
    this.starObject.fillCircle(x, y, size * 1.5);

    // 核心
    this.starObject.fillStyle(color, 1);
    this.starObject.fillCircle(x, y, size);

    // 星芒（四条射线）
    this.starObject.lineStyle(2, color, 0.8);
    for (let i = 0; i < 4; i++) {
      const angle = (i * 90) * (Math.PI / 180);
      this.starObject.beginPath();
      this.starObject.moveTo(x, y);
      this.starObject.lineTo(
        x + Math.cos(angle) * (size + 20),
        y + Math.sin(angle) * (size + 20)
      );
      this.starObject.strokePath();
    }
  }

  /**
   * 创建观察者
   */
  private createObserver(): void {
    this.observerObject = this.add.graphics();

    // 观察者位置（固定）
    const observerX = 650;
    const observerY = GAME_HEIGHT / 2;

    // 绘制望远镜图标
    this.observerObject.fillStyle(0x4a90d9, 1);
    this.observerObject.fillCircle(observerX, observerY, 15);

    // 观察者眼睛（简化）
    this.observerObject.fillStyle(0xffffff, 1);
    this.observerObject.fillCircle(observerX - 8, observerY - 5, 4);
    this.observerObject.fillCircle(observerX + 8, observerY - 5, 4);

    // 标签
    this.add.text(observerX, observerY + 35, '观察者', {
      fontSize: '12px',
      color: '#4a90d9',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 创建光束可视化
   */
  private createLightBeam(): void {
    this.lightBeamGraphics = this.add.graphics();
    this.updateLightBeamEffect();
  }

  /**
   * 更新光束效果
   */
  private updateLightBeamEffect(): void {
    if (!this.lightBeamGraphics) return;

    this.lightBeamGraphics.clear();

    // 根据多普勒效应计算颜色
    const color = this.calculateShiftedColor();

    // 绘制光束（从星星到观察者）
    const startX = 150;
    const startY = GAME_HEIGHT / 2;
    const endX = 650;
    const endY = GAME_HEIGHT / 2;

    // 光束渐变效果（多条线条模拟）
    for (let i = 0; i < 5; i++) {
      const offset = (i - 2) * 3;
      const alpha = 0.3 - Math.abs(i - 2) * 0.05;

      this.lightBeamGraphics.lineStyle(3 - Math.abs(i - 2), Phaser.Display.Color.GetColor(color.r, color.g, color.b), alpha);
      this.lightBeamGraphics.beginPath();
      this.lightBeamGraphics.moveTo(startX + 30, startY + offset);
      this.lightBeamGraphics.lineTo(endX - 15, endY + offset);
      this.lightBeamGraphics.strokePath();
    }

    // 波动效果（模拟光波）
    if (this.gameState === 'running') {
      const waveCount = this.isApproaching ? 15 : 8; // 蓝移时波长短，红移时波长长
      const waveAmplitude = 5;

      this.lightBeamGraphics.lineStyle(1, Phaser.Display.Color.GetColor(color.r, color.g, color.b), 0.5);

      for (let w = 0; w < waveCount; w++) {
        const waveX = startX + 30 + (w * (endX - startX - 45) / waveCount);
        const wavePhase = (this.elapsedTime * 5 + w) % (2 * Math.PI);

        this.lightBeamGraphics.beginPath();
        this.lightBeamGraphics.moveTo(waveX, startY - waveAmplitude * Math.sin(wavePhase));
        this.lightBeamGraphics.lineTo(waveX + 10, startY + waveAmplitude * Math.sin(wavePhase + Math.PI / 2));
        this.lightBeamGraphics.strokePath();
      }
    }
  }

  /**
   * 创建颜色预览
   */
  private createColorPreview(): void {
    this.colorPreviewGraphics = this.add.graphics();

    // 原始颜色框
    this.colorPreviewGraphics.fillStyle(0xffffff, 1);
    this.colorPreviewGraphics.fillRect(280, 80, 60, 40);
    this.colorPreviewGraphics.lineStyle(2, 0x888888);
    this.colorPreviewGraphics.strokeRect(280, 80, 60, 40);

    this.add.text(310, 60, '原始颜色', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 移位后颜色框（动态更新）
    this.updateColorPreview();

    this.add.text(380, 60, '观测颜色', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 方向箭头
    this.colorPreviewGraphics.lineStyle(2, 0x888888);
    this.colorPreviewGraphics.beginPath();
    this.colorPreviewGraphics.moveTo(345, 100);
    this.colorPreviewGraphics.lineTo(365, 100);
    this.colorPreviewGraphics.strokePath();

    // 箭头头部
    this.colorPreviewGraphics.fillStyle(0x888888);
    this.colorPreviewGraphics.fillTriangle(365, 95, 365, 105, 375, 100);
  }

  /**
   * 更新颜色预览
   */
  private updateColorPreview(): void {
    if (!this.colorPreviewGraphics) return;

    const color = this.calculateShiftedColor();
    const phaserColor = Phaser.Display.Color.GetColor(color.r, color.g, color.b);

    // 清除旧的移位颜色框
    this.colorPreviewGraphics.fillStyle(phaserColor, 1);
    this.colorPreviewGraphics.fillRect(360, 80, 60, 40);
    this.colorPreviewGraphics.lineStyle(2, this.isApproaching ? 0x4a90d9 : 0xff6b6b);
    this.colorPreviewGraphics.strokeRect(360, 80, 60, 40);
  }

  /**
   * 创建光谱条
   */
  private createSpectrumBar(): void {
    this.spectrumBar = this.add.graphics();

    // 绘制完整光谱（从紫到红）
    const barWidth = 300;
    const barHeight = 20;
    const startX = 250;
    const startY = 150;

    // 光谱颜色渐变
    const spectrumColors = [
      { r: 148, g: 0, b: 211 },   // 紫
      { r: 75, g: 0, b: 130 },    // 深紫
      { r: 0, g: 0, b: 255 },     // 蓝
      { r: 0, g: 191, b: 255 },   // 浅蓝
      { r: 0, g: 255, b: 255 },   // 青
      { r: 0, g: 255, b: 0 },     // 绿
      { r: 255, g: 255, b: 0 },   // 黄
      { r: 255, g: 165, b: 0 },   // 橙
      { r: 255, g: 0, b: 0 },     // 红
    ];

    const segmentWidth = barWidth / spectrumColors.length;

    spectrumColors.forEach((color, i) => {
      const phaserColor = Phaser.Display.Color.GetColor(color.r, color.g, color.b);
      this.spectrumBar?.fillStyle(phaserColor, 1);
      this.spectrumBar?.fillRect(startX + i * segmentWidth, startY, segmentWidth, barHeight);
    });

    // 边框
    this.spectrumBar.lineStyle(1, 0x888888);
    this.spectrumBar.strokeRect(startX, startY, barWidth, barHeight);

    // 标签
    this.add.text(startX, startY - 15, '紫（高频）', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    this.add.text(startX + barWidth - 30, startY - 15, '红（低频）', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 当前波长指示器
    this.updateWavelengthIndicator();
  }

  /**
   * 更新波长指示器
   */
  private updateWavelengthIndicator(): void {
    if (!this.spectrumBar || !this.currentDopplerResult) return;

    // 清除旧指示器
    const startX = 250;
    const startY = 150;
    const barWidth = 300;
    const barHeight = 20;

    // 计算指示器位置
    // 波长比率 > 1 表示红移（波长变长），< 1 表示蓝移（波长变短）
    const wavelengthRatio = this.currentDopplerResult.wavelengthRatio;

    // 将波长比率映射到光谱位置
    // 原始波长（白光）在中间位置 = 0.5
    // 波长比率 > 1 → 向右移动（红移）
    // 波长比率 < 1 → 向左移动（蓝移）
    let position = 0.5; // 原始位置

    if (wavelengthRatio > 1) {
      // 红移：向右移动，最大到 0.9
      position = 0.5 + Math.min((wavelengthRatio - 1) * 0.4, 0.4);
    } else {
      // 蓝移：向左移动，最小到 0.1
      position = 0.5 - Math.min((1 - wavelengthRatio) * 0.4, 0.4);
    }

    const indicatorX = startX + position * barWidth;

    // 绘制指示器（三角形）
    this.spectrumBar.fillStyle(this.isApproaching ? 0x4a90d9 : 0xff6b6b, 1);
    this.spectrumBar.fillTriangle(
      indicatorX - 5, startY + barHeight + 5,
      indicatorX + 5, startY + barHeight + 5,
      indicatorX, startY + barHeight + 15
    );

    // 指示器标签
    const label = this.isApproaching ? '蓝移' : '红移';
    this.add.text(indicatorX, startY + barHeight + 25, label, {
      fontSize: '10px',
      color: this.isApproaching ? '#4a90d9' : '#ff6b6b',
      fontFamily: 'Arial',
    }).setOrigin(0.5);
  }

  /**
   * 计算移位后的颜色
   */
  private calculateShiftedColor(): { r: number; g: number; b: number } {
    if (!this.currentDopplerResult) {
      return COLORS.white;
    }

    const frequencyRatio = this.currentDopplerResult.frequencyRatio;

    // 频率比率 > 1 表示蓝移（频率增加），< 1 表示红移（频率减少）
    if (this.isApproaching) {
      // 蓝移：向蓝色方向移动
      const shiftIntensity = Math.min((frequencyRatio - 1) * 2, 1);
      return {
        r: Math.floor(COLORS.white.r - shiftIntensity * (COLORS.white.r - COLORS.blueShift.r)),
        g: Math.floor(COLORS.white.g - shiftIntensity * (COLORS.white.g - COLORS.blueShift.g)),
        b: Math.floor(COLORS.white.b - shiftIntensity * (COLORS.white.b - COLORS.blueShift.b)),
      };
    } else {
      // 红移：向红色方向移动
      const shiftIntensity = Math.min((1 - frequencyRatio) * 2, 1);
      return {
        r: Math.floor(COLORS.white.r - shiftIntensity * (COLORS.white.r - COLORS.redShift.r)),
        g: Math.floor(COLORS.white.g - shiftIntensity * (COLORS.white.g - COLORS.redShift.g)),
        b: Math.floor(COLORS.white.b - shiftIntensity * (COLORS.white.b - COLORS.redShift.b)),
      };
    }
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 标题
    this.add.text(GAME_WIDTH / 2, 30, '红移蓝移 — 相对论多普勒效应', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 公式显示
    this.add.text(GAME_WIDTH / 2, 180, '多普勒因子：D = √((1±β)/(1∓β))', {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      200,
      this.levelSpecific?.hint ?? '选择速度和方向，观察多普勒效应',
      {
        fontSize: '14px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 数据显示区域
    this.createDataDisplay();

    // 速度选择按钮
    this.createVelocityButtons();

    // 方向选择按钮
    this.createDirectionButtons();

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 100, '', {
      fontSize: '24px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);

    // 物理说明区域
    this.createPhysicsInfo();
  }

  /**
   * 创建数据显示区域
   */
  private createDataDisplay(): void {
    const panelX = 280;
    const panelY = 220;
    const panelWidth = 240;
    const panelHeight = 100;

    // 数据面板背景
    const dataPanel = this.add.graphics();
    dataPanel.fillStyle(0x1a1a3a, 0.8);
    dataPanel.fillRect(panelX, panelY, panelWidth, panelHeight);
    dataPanel.lineStyle(1, 0x4a4a6a);
    dataPanel.strokeRect(panelX, panelY, panelWidth, panelHeight);

    // 多普勒因子
    this.dopplerFactorText = this.add.text(panelX + 10, panelY + 10, '多普勒因子 D: --', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // 频率比率
    this.frequencyRatioText = this.add.text(panelX + 10, panelY + 35, '频率比率 f\'/f: --', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // 波长比率
    this.wavelengthRatioText = this.add.text(panelX + 10, panelY + 60, '波长比率 λ\'/λ: --', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
    });

    // 颜色描述
    this.colorDescriptionText = this.add.text(panelX + 10, panelY + 85, '效果描述: --', {
      fontSize: '10px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 创建速度选择按钮
   */
  private createVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.velocityOptions;
    const startX = 200;
    const y = 350;
    const buttonWidth = 70;

    this.add.text(startX - 10, y - 25, '选择速度（以光速为单位）:', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    velocities.forEach((velocity, i) => {
      const x = startX + i * (buttonWidth + 10);
      const btn = this.add.graphics();
      const isSelected = this.selectedVelocity === velocity;

      btn.fillStyle(isSelected ? 0x4ade80 : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRect(x, y, buttonWidth, 35);
      btn.lineStyle(2, isSelected ? 0x4ade80 : 0x6a6a8a);
      btn.strokeRect(x, y, buttonWidth, 35);

      this.add.text(x + buttonWidth / 2, y + 17, `${velocity.toFixed(1)}c`, {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      btn.setInteractive(
        new Phaser.Geom.Rectangle(x, y, buttonWidth, 35),
        Phaser.Geom.Rectangle.Contains
      );

      btn.on('pointerdown', () => {
        if (this.gameState === 'idle') {
          this.selectedVelocity = velocity;
          this.updateVelocityButtons();
          this.updateDopplerCalculation();
        }
      });

      btn.on('pointerover', () => {
        if (this.gameState === 'idle') {
          btn.clear();
          btn.fillStyle(0x4ade80, 0.6);
          btn.fillRect(x, y, buttonWidth, 35);
          btn.strokeRect(x, y, buttonWidth, 35);
        }
      });

      btn.on('pointerout', () => {
        if (this.gameState === 'idle') {
          this.updateVelocityButtons();
        }
      });

      this.velocityButtons.push(btn);
    });
  }

  /**
   * 更新速度按钮状态
   */
  private updateVelocityButtons(): void {
    if (!this.levelSpecific) return;

    const velocities = this.levelSpecific.velocityOptions;
    const startX = 200;
    const y = 350;
    const buttonWidth = 70;

    this.velocityButtons.forEach((btn, i) => {
      const velocity = velocities[i];
      if (velocity === undefined) return;
      const isSelected = this.selectedVelocity === velocity;

      btn.clear();
      btn.fillStyle(isSelected ? 0x4ade80 : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRect(startX + i * (buttonWidth + 10), y, buttonWidth, 35);
      btn.lineStyle(2, isSelected ? 0x4ade80 : 0x6a6a8a);
      btn.strokeRect(startX + i * (buttonWidth + 10), y, buttonWidth, 35);
    });
  }

  /**
   * 创建方向选择按钮
   */
  private createDirectionButtons(): void {
    const startX = 540;
    const y = 350;

    this.add.text(startX, y - 25, '运动方向:', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 接近按钮（蓝移）
    const approachBtn = this.add.graphics();
    approachBtn.fillStyle(this.isApproaching ? 0x4a90d9 : 0x4a4a6a, this.isApproaching ? 1 : 0.8);
    approachBtn.fillRect(startX, y, 100, 35);
    approachBtn.lineStyle(2, this.isApproaching ? 0x4a90d9 : 0x6a6a8a);
    approachBtn.strokeRect(startX, y, 100, 35);

    this.add.text(startX + 50, y + 17, '接近（蓝移）', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    approachBtn.setInteractive(
      new Phaser.Geom.Rectangle(startX, y, 100, 35),
      Phaser.Geom.Rectangle.Contains
    );

    approachBtn.on('pointerdown', () => {
      if (this.gameState === 'idle') {
        this.isApproaching = true;
        this.updateDirectionButtons();
        this.updateDopplerCalculation();
      }
    });

    // 远离按钮（红移）
    const recedeBtn = this.add.graphics();
    recedeBtn.fillStyle(!this.isApproaching ? 0xff6b6b : 0x4a4a6a, !this.isApproaching ? 1 : 0.8);
    recedeBtn.fillRect(startX + 110, y, 100, 35);
    recedeBtn.lineStyle(2, !this.isApproaching ? 0xff6b6b : 0x6a6a8a);
    recedeBtn.strokeRect(startX + 110, y, 100, 35);

    this.add.text(startX + 160, y + 17, '远离（红移）', {
      fontSize: '12px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    recedeBtn.setInteractive(
      new Phaser.Geom.Rectangle(startX + 110, y, 100, 35),
      Phaser.Geom.Rectangle.Contains
    );

    recedeBtn.on('pointerdown', () => {
      if (this.gameState === 'idle') {
        this.isApproaching = false;
        this.updateDirectionButtons();
        this.updateDopplerCalculation();
      }
    });

    this.directionButtons.push(approachBtn, recedeBtn);
  }

  /**
   * 更新方向按钮状态
   */
  private updateDirectionButtons(): void {
    const startX = 540;
    const y = 350;

    // 接近按钮
    const approachBtn = this.directionButtons[0];
    if (approachBtn) {
      approachBtn.clear();
      approachBtn.fillStyle(this.isApproaching ? 0x4a90d9 : 0x4a4a6a, this.isApproaching ? 1 : 0.8);
      approachBtn.fillRect(startX, y, 100, 35);
      approachBtn.lineStyle(2, this.isApproaching ? 0x4a90d9 : 0x6a6a8a);
      approachBtn.strokeRect(startX, y, 100, 35);
    }

    // 远离按钮
    const recedeBtn = this.directionButtons[1];
    if (recedeBtn) {
      recedeBtn.clear();
      recedeBtn.fillStyle(!this.isApproaching ? 0xff6b6b : 0x4a4a6a, !this.isApproaching ? 1 : 0.8);
      recedeBtn.fillRect(startX + 110, y, 100, 35);
      recedeBtn.lineStyle(2, !this.isApproaching ? 0xff6b6b : 0x6a6a8a);
      recedeBtn.strokeRect(startX + 110, y, 100, 35);
    }
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(GAME_WIDTH / 2 - 60, 420, 120, 45);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(GAME_WIDTH / 2 - 60, 420, 120, 45);

    this.add.text(GAME_WIDTH / 2, 442, '开始观测', {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(GAME_WIDTH / 2 - 60, 420, 120, 45),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startObservation());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRect(GAME_WIDTH / 2 - 60, 420, 120, 45);
      this.startButton?.strokeRect(GAME_WIDTH / 2 - 60, 420, 120, 45);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRect(GAME_WIDTH / 2 - 60, 420, 120, 45);
      this.startButton?.strokeRect(GAME_WIDTH / 2 - 60, 420, 120, 45);
    });
  }

  /**
   * 创建物理信息显示
   */
  private createPhysicsInfo(): void {
    // 显示核心公式
    this.add.text(50, GAME_HEIGHT - 80, '接近时：D = √((1+β)/(1-β)) > 1（蓝移）', {
      fontSize: '11px',
      color: '#4a90d9',
      fontFamily: 'Arial',
    });

    this.add.text(50, GAME_HEIGHT - 60, '远离时：D = √((1-β)/(1+β)) < 1（红移）', {
      fontSize: '11px',
      color: '#ff6b6b',
      fontFamily: 'Arial',
    });

    this.add.text(50, GAME_HEIGHT - 40, '其中 β = v/c（速度与光速的比率）', {
      fontSize: '11px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 天文学应用
    this.add.text(400, GAME_HEIGHT - 60, '天文学应用：', {
      fontSize: '11px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    });

    this.add.text(400, GAME_HEIGHT - 40, '恒星红移 → 宇宙膨胀（哈勃定律）', {
      fontSize: '11px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 更新多普勒计算
   */
  private updateDopplerCalculation(): void {
    try {
      // 使用 Relativity 模块计算多普勒效应
      this.currentDopplerResult = this.relativity.dopplerEffect(
        this.selectedVelocity,
        this.isApproaching
      );

      // 更新数据显示
      if (this.dopplerFactorText) {
        this.dopplerFactorText.setText(`多普勒因子 D: ${this.currentDopplerResult.factor.toFixed(4)}`);
      }

      if (this.frequencyRatioText) {
        const freqRatio = this.currentDopplerResult.frequencyRatio;
        const freqColor = this.isApproaching ? '#4a90d9' : '#ff6b6b';
        this.frequencyRatioText.setText(`频率比率 f'/f: ${freqRatio.toFixed(4)}`);
        this.frequencyRatioText.setColor(freqColor);
      }

      if (this.wavelengthRatioText) {
        const waveRatio = this.currentDopplerResult.wavelengthRatio;
        this.wavelengthRatioText.setText(`波长比率 λ'/λ: ${waveRatio.toFixed(4)}`);
      }

      if (this.colorDescriptionText) {
        this.colorDescriptionText.setText(this.currentDopplerResult.description);
      }

      // 更新颜色预览
      this.updateColorPreview();

      // 更新光谱指示器
      this.updateWavelengthIndicator();

      // 更新星星颜色
      const color = this.calculateShiftedColor();
      this.drawStar(150, GAME_HEIGHT / 2, 30, Phaser.Display.Color.GetColor(color.r, color.g, color.b));

      // 更新光束
      this.updateLightBeamEffect();

      // 更新提示
      this.updateHint();
    } catch (error) {
      console.error('多普勒计算错误:', error);
    }
  }

  /**
   * 更新提示文本
   */
  private updateHint(): void {
    if (!this.currentDopplerResult) return;

    const effect = this.isApproaching ? '蓝移' : '红移';
    const colorChange = this.isApproaching
      ? '颜色向蓝色移动（频率增加）'
      : '颜色向红色移动（频率减少）';

    this.hintText?.setText(
      `速度 ${this.selectedVelocity.toFixed(1)}c，${effect}效应：${colorChange}`
    );
  }

  /**
   * 动画光源移动
   */
  private animateStarMovement(): void {
    if (!this.starObject) return;

    const baseX = 150;
    const moveSpeed = this.selectedVelocity * 50; // 游戏动画速度
    const direction = this.isApproaching ? 1 : -1; // 接近时向右，远离时向左

    // 光源位置变化（简化动画）
    const offsetX = direction * Math.sin(this.elapsedTime * moveSpeed * 0.1) * 20;
    const currentX = baseX + offsetX;

    // 更新星星位置和颜色
    const color = this.calculateShiftedColor();
    this.drawStar(currentX, GAME_HEIGHT / 2, 30, Phaser.Display.Color.GetColor(color.r, color.g, color.b));

    // 更新光束起点
    this.updateLightBeamEffect();
  }

  /**
   * 开始观测
   */
  private startObservation(): void {
    if (this.gameState !== 'idle') return;

    this.gameState = 'running';
    this.elapsedTime = 0;

    // 更新按钮状态
    this.updateStartButtonState('running');

    // 更新提示
    this.hintText?.setText('观测中...观察颜色变化效果');

    // 自动完成计时器（3秒后自动完成）
    this.time.delayedCall(3000, () => {
      this.checkObservationComplete();
    });
  }

  /**
   * 检查观测完成
   */
  private checkObservationComplete(): void {
    if (this.gameState !== 'running') return;

    // 检查是否达到目标多普勒因子范围
    if (this.levelSpecific && this.currentDopplerResult) {
      const dopplerFactor = this.currentDopplerResult.factor;
      const targetMin = this.levelSpecific.targetDopplerMin;
      const targetMax = this.levelSpecific.targetDopplerMax;

      if (dopplerFactor >= targetMin && dopplerFactor <= targetMax) {
        this.gameState = 'success';
        this.showSuccessResult();
      } else {
        // 即使不在范围内，也算完成（教学关卡，主要是让玩家理解概念）
        this.gameState = 'success';
        this.showSuccessResult();
      }
    } else {
      this.gameState = 'success';
      this.showSuccessResult();
    }

    this.updateStartButtonState('idle');
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
    } else {
      this.startButton.fillStyle(0x4ade80, 1);
    }
    this.startButton.fillRect(GAME_WIDTH / 2 - 60, 420, 120, 45);
    this.startButton.strokeRect(GAME_WIDTH / 2 - 60, 420, 120, 45);
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(14, this.elapsedTime);

    const effect = this.isApproaching ? '蓝移' : '红移';
    const dopplerFactor = this.currentDopplerResult?.factor.toFixed(4) ?? '--';

    this.resultText?.setText(`✅ 观测完成！${effect}因子 D=${dopplerFactor}`)
      .setColor(this.isApproaching ? '#4a90d9' : '#ff6b6b')
      .setVisible(true);

    this.hintText?.setText('多普勒效应解释了天文学中的红移现象，支持宇宙膨胀理论！');

    this.createCompletionButtons();
  }

  /**
   * 创建完成后的按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 150;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '16px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(15)) {
        this.scene.start('Level15Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const retryBtn = this.add.text(GAME_WIDTH / 2, y, '重试', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    retryBtn.on('pointerdown', () => {
      this.resetObservation();
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 重置观测
   */
  private resetObservation(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;

    // 重置星星位置
    this.drawStar(150, GAME_HEIGHT / 2, 30, Phaser.Display.Color.GetColor(255, 255, 255));

    // 清除光束动画
    this.updateLightBeamEffect();

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '选择速度和方向，观察多普勒效应');

    // 更新按钮状态
    this.updateStartButtonState('idle');

    // 重新计算
    this.updateDopplerCalculation();
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'Enter') {
        this.startObservation();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetObservation();
      }
    });
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-14');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 14,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取当前多普勒结果
   */
  getDopplerResult(): DopplerResult | null {
    return this.currentDopplerResult;
  }

  /**
   * 获取选中的速度
   */
  getSelectedVelocity(): number {
    return this.selectedVelocity;
  }

  /**
   * 获取运动方向
   */
  getIsApproaching(): boolean {
    return this.isApproaching;
  }
}